// src/components/Header.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, LogOut, User, Crown, BadgeCheck } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';   // ⭐ NEW
import logo from '../assets/lumochart-logo.png';

export default function Header() {
  const navigate = useNavigate();
  
  const { logout } = useAuth();
  const { profile } = useUserProfile();
  const { theme, toggleTheme } = useTheme();

  // ⭐ NEW unified subscription status
  const { isPro, isDev } = useSubscription();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();

  // --- Dev Mode TOAST ---
  const [showDevToast, setShowDevToast] = useState(false);

  useEffect(() => {
    if (isDev && !sessionStorage.getItem("devToastShown")) {
      setShowDevToast(true);
      sessionStorage.setItem("devToastShown", "true");
      setTimeout(() => setShowDevToast(false), 5000);
    }
  }, [isDev]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Extract the user's display name + initials
  const fullName = profile?.fullName || "";
  const title = profile?.title?.toUpperCase() || "";

  let greetingName;
  let initials;

  if (!fullName) {
    greetingName = "Clinician";
    initials = "C";
  } else {
    const nameParts = fullName.trim().split(" ");
    const lastName = nameParts[nameParts.length - 1] || "";
    initials = (nameParts[0]?.[0] || '') + (lastName?.[0] || '');

    if (['MD', 'DO'].includes(title)) {
      greetingName = `Dr. ${lastName}`;
    } else if (['NP', 'PA', 'RN'].includes(title)) {
      greetingName = `${title} ${lastName}`;
    } else {
      greetingName = fullName;
    }
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Toast */}
      {showDevToast && (
        <div className="fixed top-6 right-6 z-[9999] bg-purple-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm animate-fadeInDown">
          <BadgeCheck size={18} className="text-white" />
          <span>Developer Mode Enabled — Full Access</span>
        </div>
      )}

      <header className="sticky top-0 z-50 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-lg shadow-sm px-6 py-3 flex justify-between items-center">
        
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2 text-gray-900 dark:text-white hover:opacity-85">
          <img src={logo} alt="LumoChart Logo" className="h-8 w-8" />
          <span className="text-lg font-bold">LumoChart</span>
        </Link>

        {/* Right Avatar Menu */}
        <div className="relative" ref={menuRef}>
          <button
            className="flex items-center gap-3 text-gray-800 dark:text-gray-100 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {/* Avatar */}
            <div className="w-9 h-9 bg-blue-600 text-white font-semibold rounded-full flex items-center justify-center text-sm">
              {initials}
            </div>

            {/* Greeting */}
            <div className="hidden md:block text-sm font-medium">
              Welcome back, <strong>{greetingName}</strong>
            </div>

            {/* ⭐ BADGE AREA */}
            <div className="hidden md:flex items-center gap-1">
              {isDev && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-600/20 text-purple-600 dark:text-purple-300 border border-purple-500/40">
                  DEV
                </span>
              )}
              {!isDev && isPro && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-600 dark:text-yellow-300 border border-yellow-500/40">
                  PRO
                </span>
              )}
            </div>
          </button>

          {/* Dropdown Menu */}
          {menuOpen && (
            <div className="absolute right-0 mt-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-zinc-700 w-56">

              {/* Edit Profile */}
              <button
                onClick={() => { navigate('/profile'); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-700"
              >
                <User size={16} /> Edit Profile
              </button>

              {/* Upgrade to Pro (hidden if already Pro or Dev) */}
              {!isPro && !isDev && (
                <button
                  onClick={() => { navigate('/upgrade'); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 font-medium"
                >
                  <Crown size={16} /> Upgrade to Pro
                </button>
              )}

              {/* Dark Mode */}
              <button
                onClick={toggleTheme}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-700"
              >
                {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
              </button>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-zinc-700 my-1"></div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-zinc-700"
              >
                <LogOut size={16} /> Logout
              </button>

            </div>
          )}
        </div>
      </header>
    </>
  );
}
